import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  LogOut, Menu, Eye, Mail, KeyRound, Smartphone,
  Percent, Trash2, Search, X, AlertTriangle, Star, RotateCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadCustomers();
  }, [adminToken, navigate]);

  const loadCustomers = async (search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers${search ? `?search=${search}` : ''}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error loading customers:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewCustomerDetails = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedCustomer(response.data);
    } catch (error) {
      console.error('Error loading customer details:', error);
      toast.error('Kon klantgegevens niet laden');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCustomers(searchQuery);
  };

  const handleResetPassword = async () => {
    if (!selectedCustomer) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/customers/${selectedCustomer.customer.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Nieuw wachtwoord verzonden naar de klant');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wachtwoord reset mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset2FA = async () => {
    if (!selectedCustomer) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/customers/${selectedCustomer.customer.id}/reset-2fa`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('2FA gereset - klant kan opnieuw koppelen');
      // Refresh customer data
      viewCustomerDetails(selectedCustomer.customer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || '2FA reset mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendDiscount = async (percent) => {
    if (!selectedCustomer) return;
    
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${selectedCustomer.customer.id}/send-discount`,
        { discount_percent: percent },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`${percent}% kortingscode verzonden: ${response.data.code}`);
      setShowDiscountModal(false);
      // Refresh customer data
      viewCustomerDetails(selectedCustomer.customer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kortingscode versturen mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    setActionLoading(true);
    try {
      await axios.delete(
        `${API}/admin/customers/${selectedCustomer.customer.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Klant en alle gekoppelde data verwijderd');
      setShowDeleteConfirm(false);
      setSelectedCustomer(null);
      loadCustomers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Bestellingen', icon: ShoppingCart },
    { path: '/admin/products', label: 'Producten', icon: Package },
    { path: '/admin/reviews', label: 'Reviews', icon: Star },
    { path: '/admin/360-views', label: '360° Views', icon: RotateCw },
    { path: '/admin/customers', label: 'Klanten', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 glass-strong transform transition-transform duration-300
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#2C1B18]">
            <h1 className="font-heading text-xl">
              <span className="gold-text">Zuiver</span>
              <span className="text-white">Parfum</span>
            </h1>
            <p className="text-[#57534E] text-xs mt-1">Admin Portal</p>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-[#A8A29E] hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-[#2C1B18]">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 w-full transition-colors"
            >
              <LogOut size={20} />
              Uitloggen
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="glass-strong sticky top-0 z-30 px-4 md:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-white">
              <Menu size={24} />
            </button>
            <h2 className="font-heading text-xl text-white">Klanten</h2>
          </div>
          
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#57534E]" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoeken op naam of email..."
                className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#57534E] focus:border-[#D4AF37] outline-none"
              />
            </div>
          </form>
          
          <span className="text-[#A8A29E] text-sm whitespace-nowrap">{customers.length} klanten</span>
        </header>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-[#A8A29E] text-sm border-b border-[#2C1B18]">
                      <th className="text-left py-4 px-4">Klant</th>
                      <th className="text-left py-4 px-4">Email</th>
                      <th className="text-left py-4 px-4 hidden md:table-cell">Telefoon</th>
                      <th className="text-center py-4 px-4 hidden md:table-cell">Bestellingen</th>
                      <th className="text-center py-4 px-4 hidden md:table-cell">2FA</th>
                      <th className="text-left py-4 px-4 hidden lg:table-cell">Aangemeld</th>
                      <th className="text-right py-4 px-4">Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-[#57534E]">
                          Geen klanten gevonden
                        </td>
                      </tr>
                    ) : (
                      customers.map((customer) => (
                        <tr 
                          key={customer.id} 
                          className="border-b border-[#2C1B18]/50 hover:bg-white/5 cursor-pointer"
                          onClick={() => viewCustomerDetails(customer.id)}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-[#D4AF37] font-medium">
                                  {customer.first_name?.[0]}{customer.last_name?.[0]}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-white truncate">
                                  {customer.first_name} {customer.last_name}
                                </p>
                                <p className="text-[#57534E] text-xs truncate">
                                  {customer.loyalty_points || 0} punten • {customer.tier || 'Bronze'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-[#A8A29E] truncate max-w-[200px]">{customer.email}</td>
                          <td className="py-4 px-4 text-[#A8A29E] hidden md:table-cell">{customer.phone || '-'}</td>
                          <td className="py-4 px-4 text-center text-white hidden md:table-cell">
                            {customer.order_count || 0}
                          </td>
                          <td className="py-4 px-4 text-center hidden md:table-cell">
                            {customer.has_2fa ? (
                              <span className="text-green-500">✓</span>
                            ) : (
                              <span className="text-[#57534E]">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-[#A8A29E] text-sm hidden lg:table-cell">
                            {customer.created_at ? new Date(customer.created_at).toLocaleDateString('nl-NL') : '-'}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); viewCustomerDetails(customer.id); }}
                              className="p-2 hover:bg-white/10 rounded-lg text-[#A8A29E] hover:text-[#D4AF37] transition-colors"
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Customer Detail Modal */}
        {selectedCustomer && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="font-heading text-xl text-white">
                    {selectedCustomer.customer.first_name} {selectedCustomer.customer.last_name}
                  </h3>
                  <p className="text-[#A8A29E] text-sm flex items-center gap-2 mt-1">
                    <Mail size={14} />
                    {selectedCustomer.customer.email}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded">
                      {selectedCustomer.customer.tier || 'Bronze'} Tier
                    </span>
                    <span className="text-[#57534E] text-xs">
                      {selectedCustomer.customer.loyalty_points || 0} punten
                    </span>
                    {selectedCustomer.customer.is_2fa_enabled && (
                      <span className="text-green-500 text-xs flex items-center gap-1">
                        <Smartphone size={12} /> 2FA actief
                      </span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedCustomer(null)} 
                  className="text-[#A8A29E] hover:text-white p-1"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <button
                  onClick={handleResetPassword}
                  disabled={actionLoading}
                  className="flex flex-col items-center gap-2 p-4 bg-[#1C1917] hover:bg-[#2C1B18] rounded-xl transition-colors"
                >
                  <KeyRound size={24} className="text-[#D4AF37]" />
                  <span className="text-white text-sm">Wachtwoord Reset</span>
                </button>
                <button
                  onClick={handleReset2FA}
                  disabled={actionLoading}
                  className="flex flex-col items-center gap-2 p-4 bg-[#1C1917] hover:bg-[#2C1B18] rounded-xl transition-colors"
                >
                  <Smartphone size={24} className="text-blue-500" />
                  <span className="text-white text-sm">2FA Reset</span>
                </button>
                <button
                  onClick={() => setShowDiscountModal(true)}
                  disabled={actionLoading}
                  className="flex flex-col items-center gap-2 p-4 bg-[#1C1917] hover:bg-[#2C1B18] rounded-xl transition-colors"
                >
                  <Percent size={24} className="text-green-500" />
                  <span className="text-white text-sm">Kortingscode</span>
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={actionLoading}
                  className="flex flex-col items-center gap-2 p-4 bg-[#1C1917] hover:bg-red-900/20 rounded-xl transition-colors"
                >
                  <Trash2 size={24} className="text-red-500" />
                  <span className="text-white text-sm">Verwijderen</span>
                </button>
              </div>

              {/* Discount Codes */}
              {selectedCustomer.discount_codes?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-white font-medium mb-3">Kortingscodes</h4>
                  <div className="space-y-2">
                    {selectedCustomer.discount_codes.map((code) => (
                      <div key={code.id} className="bg-[#1C1917] rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <code className="text-[#D4AF37] font-mono">{code.code}</code>
                          <p className="text-[#57534E] text-xs">{code.discount_percent}% korting</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${code.is_used ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
                          {code.is_used ? 'Gebruikt' : 'Actief'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orders */}
              <div className="mb-6">
                <h4 className="text-white font-medium mb-3">Bestellingen ({selectedCustomer.orders.length})</h4>
                {selectedCustomer.orders.length === 0 ? (
                  <p className="text-[#57534E]">Geen bestellingen</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedCustomer.orders.map((order) => (
                      <div key={order.id} className="bg-[#1C1917] rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="text-white text-sm">#{order.id.slice(0, 8)}</p>
                          <p className="text-[#57534E] text-xs">
                            {new Date(order.created_at).toLocaleDateString('nl-NL')} • {order.items?.length || 0} items
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[#D4AF37]">€{order.total?.toFixed(2)}</p>
                          <p className={`text-xs ${
                            order.status === 'delivered' ? 'text-green-500' :
                            order.status === 'shipped' ? 'text-blue-500' :
                            order.status === 'cancelled' ? 'text-red-500' : 'text-yellow-500'
                          }`}>{order.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Addresses */}
              <div>
                <h4 className="text-white font-medium mb-3">Adressen ({selectedCustomer.addresses.length})</h4>
                {selectedCustomer.addresses.length === 0 ? (
                  <p className="text-[#57534E]">Geen adressen opgeslagen</p>
                ) : (
                  <div className="space-y-2">
                    {selectedCustomer.addresses.map((address) => (
                      <div key={address.id} className="bg-[#1C1917] rounded-lg p-3">
                        <p className="text-white text-sm">{address.label} {address.is_default && <span className="text-[#D4AF37] text-xs">(standaard)</span>}</p>
                        <p className="text-[#57534E] text-xs">
                          {address.street} {address.house_number}, {address.postal_code} {address.city}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Discount Modal */}
        {showDiscountModal && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-white text-xl font-medium mb-4 text-center">Kortingscode Versturen</h3>
              <p className="text-[#A8A29E] text-sm text-center mb-6">
                Selecteer het kortingspercentage. De code is eenmalig geldig.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => handleSendDiscount(10)}
                  disabled={actionLoading}
                  className="p-6 bg-[#1C1917] hover:bg-[#D4AF37]/20 border-2 border-transparent hover:border-[#D4AF37] rounded-xl transition-all"
                >
                  <p className="text-3xl font-bold text-[#D4AF37]">10%</p>
                  <p className="text-[#A8A29E] text-sm">korting</p>
                </button>
                <button
                  onClick={() => handleSendDiscount(20)}
                  disabled={actionLoading}
                  className="p-6 bg-[#1C1917] hover:bg-[#D4AF37]/20 border-2 border-transparent hover:border-[#D4AF37] rounded-xl transition-all"
                >
                  <p className="text-3xl font-bold text-[#D4AF37]">20%</p>
                  <p className="text-[#A8A29E] text-sm">korting</p>
                </button>
              </div>
              <button
                onClick={() => setShowDiscountModal(false)}
                className="w-full px-4 py-3 border border-[#2C1B18] rounded-lg text-[#A8A29E] hover:text-white transition-colors"
              >
                Annuleren
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-6 max-w-sm w-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle size={32} className="text-red-500" />
                </div>
                <h3 className="text-white text-xl font-medium mb-2">Account Verwijderen</h3>
                <p className="text-[#A8A29E] text-sm">
                  Weet je zeker dat je dit account wilt verwijderen? 
                  Alle gekoppelde data (bestellingen, adressen, reviews, kortingscodes) wordt permanent verwijderd.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 border border-[#2C1B18] rounded-lg text-[#A8A29E] hover:text-white transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteCustomer}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition-colors flex items-center justify-center"
                >
                  {actionLoading ? <div className="spinner"></div> : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCustomers;
